import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { Contact, Company, Deal, SaveToCrmConfig, SaveToCrmResponse } from '../state/crm/crm.state';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class CrmService {
  private apiUrl = environment.api.url;

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getHttpOptions(): Observable<{ headers: HttpHeaders }> {
    return this.authService.token().pipe(
      map(token => ({
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        }),
      }))
    );
  }

  /**
   * Translate {sortBy, sortDir} to Spatie QueryBuilder's single-string sort.
   *  - 'created_at'                                       → 'created_at' / '-created_at'
   *  - any name token (person_name|company_name|deal_name) → nameColumn / '-nameColumn'
   *  - omitted or unknown                                 → '-created_at' (default)
   */
  private toSpatieSort(
    sortBy: 'created_at' | 'person_name' | 'company_name' | 'deal_name' | undefined,
    sortDir: 'asc' | 'desc' | undefined,
    nameColumn: string
  ): string {
    const dir = sortDir === 'asc' ? 'asc' : 'desc';
    const isNameSort = sortBy === 'person_name' || sortBy === 'company_name' || sortBy === 'deal_name';
    const column = isNameSort ? nameColumn : 'created_at';
    return dir === 'desc' ? `-${column}` : column;
  }

  // Contact Methods
  getContacts(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    user_id?: number;
    filters?: {
      name?: string;
      surname?: string;
      email?: string;
      phone_number?: string;
    };
    sortBy?: 'created_at' | 'person_name' | 'company_name' | 'deal_name';
    sortDir?: 'asc' | 'desc';
    dateRange?: string;
  }): Observable<{
    data: Contact[];
    pagination: {
      total: number;
      per_page: number;
      current_page: number;
      last_page: number;
      from: number;
      to: number;
    };
    message: string;
  }> {
    const url = `${this.apiUrl}/crm-contacts`;

    let httpParams = new HttpParams();
    // Include relations: companyContacts.company for the company column,
    // search.person + search.companies so we can decorate the row with the
    // report avatar, the "View report" affordance, and per-CRMCompany logos
    // sourced from the enriched search.
    httpParams = httpParams.set('include', 'companyContacts.company,search.person,search.companies');
    // Sort — translate FE shape (sortBy + sortDir) to Spatie's single param.
    // For contacts: person_name → name column. Default kept as -created_at.
    httpParams = httpParams.set('sort', this.toSpatieSort(params?.sortBy, params?.sortDir, 'name'));

    if (params?.page) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params?.per_page) {
      httpParams = httpParams.set('per_page', params.per_page.toString());
    }

    // User ID filter for data isolation
    if (params?.user_id) {
      httpParams = httpParams.set('filter[user_id]', params.user_id.toString());
    }

    // Date-range — only attach when non-default; resolver on the API maps the token.
    if (params?.dateRange && params.dateRange !== 'all') {
      httpParams = httpParams.set('filter[date_range]', params.dateRange);
    }

    // Support new filter parameters
    if (params?.filters) {
      // Specific field filters (AND logic)
      if (params.filters.name) {
        httpParams = httpParams.set('filter[name]', params.filters.name);
      }
      if (params.filters.surname) {
        httpParams = httpParams.set('filter[surname]', params.filters.surname);
      }
      if (params.filters.email) {
        httpParams = httpParams.set('filter[email]', params.filters.email);
      }
      if (params.filters.phone_number) {
        httpParams = httpParams.set('filter[phone_number]', params.filters.phone_number);
      }
    } else if (params?.search) {
      // Search by name field only (since API doesn't support global search)
      // Note: This searches in the 'name' field which typically contains the first name
      httpParams = httpParams.set('filter[name]', params.search);
    }

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.get<any>(url, {
          ...options,
          params: httpParams
        }).pipe(
          map((response) => {
            // console.log('API Response:', response);
            if (!response || !response.data || !response.data.data) {
              throw new Error('Failed to load contacts: Invalid response structure');
            }
            // The API returns pagination data nested inside response.data
            const paginationData = response.data;
            return {
              data: paginationData.data, // The actual contacts array
              pagination: {
                total: paginationData.total,
                per_page: paginationData.per_page,
                current_page: paginationData.current_page,
                last_page: paginationData.last_page,
                from: paginationData.from,
                to: paginationData.to
              },
              message: response.metadata?.message || 'Contacts loaded successfully',
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  createContact(contactData: {
    name: string;
    surname: string;
    email: string;
    phone_number?: string;
    lead_source?: string;
    job_title?: string;
    linkedin?: string;
    personal_background?: string;
    company_id?: number;
    new_company?: {
      name: string;
      url?: string;
    };
  }): Observable<{ data: Contact; message: string }> {
    const url = `${this.apiUrl}/crm-contacts`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<any>(url, contactData, options).pipe(
          map((response) => {
            if (!response.data) {
              throw new Error('Failed to create contact: ' + (response.metadata?.message || response.message));
            }
            return {
              data: response.data,
              message: response.metadata?.message || response.message,
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  updateContact(id: number, contactData: Partial<Contact>): Observable<{ data: Contact; message: string }> {
    const url = `${this.apiUrl}/crm-contacts/${id}`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.put<any>(url, contactData, options).pipe(
          map((response) => {
            if (!response.data) {
              throw new Error('Failed to update contact: ' + (response.metadata?.message || response.message));
            }
            return {
              data: response.data,
              message: response.metadata?.message || response.message,
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  deleteContact(id: number): Observable<{ message: string }> {
    const url = `${this.apiUrl}/crm-contacts/${id}`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.delete<any>(url, options).pipe(
          map((response) => {
            return {
              message: response.metadata?.message || response.message,
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  deleteCompanyContact(id: number): Observable<{ message: string }> {
    const url = `${this.apiUrl}/crm-company-contacts/${id}`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.delete<any>(url, options).pipe(
          map((response) => {
            return {
              message: response.metadata?.message || response.message,
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  getContactDetail(id: string): Observable<{ data: Contact; message: string }> {
    const url = `${this.apiUrl}/crm-contacts/${id}`;

    let httpParams = new HttpParams();
    // Include company information for contact details
    httpParams = httpParams.set('include', 'companyContacts.company');

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.get<any>(url, {
          ...options,
          params: httpParams
        }).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Failed to load contact details: Invalid response structure');
            }
            return {
              data: response.data,
              message: response.metadata?.message || 'Contact details loaded successfully',
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  createCompanyContact(data: {
    company_id: number;
    contact_id: number;
    is_default: number;
  }): Observable<{ data: any; message: string }> {
    const url = `${this.apiUrl}/crm-company-contacts`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<any>(url, data, options).pipe(
          map((response) => {
            if (!response.data) {
              throw new Error('Failed to create company-contact relationship: ' + (response.metadata?.message || response.message));
            }
            return {
              data: response.data,
              message: response.metadata?.message || response.message,
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  // Company Methods
  getCompanies(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    user_id?: number;
    filters?: {
      name?: string;
      url?: string;
      industry?: string;
      country?: string;
    };
    sortBy?: 'created_at' | 'person_name' | 'company_name' | 'deal_name';
    sortDir?: 'asc' | 'desc';
    dateRange?: string;
  }): Observable<{
    data: any[];
    pagination: {
      total: number;
      per_page: number;
      current_page: number;
      last_page: number;
      from: number;
      to: number;
    };
    message: string;
  }> {
    const url = `${this.apiUrl}/crm-companies`;

    let httpParams = new HttpParams();
    // Include companyContacts.contact (always), plus the contact's source
    // report (search.person) so we can render the contact's avatar inside the
    // Contacts column when the contact came from a report.
    httpParams = httpParams.set('include', 'companyContacts.contact,companyContacts.contact.search.person');
    // Sort — for companies the Company Name option maps to the `name` column.
    httpParams = httpParams.set('sort', this.toSpatieSort(params?.sortBy, params?.sortDir, 'name'));

    if (params?.page) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params?.per_page) {
      httpParams = httpParams.set('per_page', params.per_page.toString());
    }

    // User ID filter for data isolation
    if (params?.user_id) {
      httpParams = httpParams.set('filter[user_id]', params.user_id.toString());
    }

    // Date-range — only attach when non-default; resolver on the API maps the token.
    if (params?.dateRange && params.dateRange !== 'all') {
      httpParams = httpParams.set('filter[date_range]', params.dateRange);
    }

    // Support new filter parameters
    if (params?.filters) {
      // Specific field filters (AND logic)
      if (params.filters.name) {
        httpParams = httpParams.set('filter[name]', params.filters.name);
      }
      if (params.filters.url) {
        httpParams = httpParams.set('filter[url]', params.filters.url);
      }
      if (params.filters.industry) {
        httpParams = httpParams.set('filter[industry]', params.filters.industry);
      }
      if (params.filters.country) {
        httpParams = httpParams.set('filter[country]', params.filters.country);
      }
    } else if (params?.search) {
      // Search by name field only
      httpParams = httpParams.set('filter[name]', params.search);
    }

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.get<any>(url, {
          ...options,
          params: httpParams
        }).pipe(
          map((response) => {
            // console.log('Companies API Response:', response);
            if (!response || !response.data) {
              throw new Error('Failed to load companies: Invalid response structure');
            }

            // Check if response has nested structure like contacts (response.data.data)
            // or if companies are directly in response.data
            let companiesArray: any[];
            let paginationData: any;

            if (response.data.data && Array.isArray(response.data.data)) {
              // Similar structure to contacts with nested pagination
              companiesArray = response.data.data;
              paginationData = response.data;
            } else if (Array.isArray(response.data)) {
              // Companies are directly in response.data
              companiesArray = response.data;
              paginationData = null;
            } else {
              throw new Error('Failed to load companies: Unexpected response structure');
            }

            return {
              data: companiesArray,
              pagination: paginationData ? {
                total: paginationData.total,
                per_page: paginationData.per_page,
                current_page: paginationData.current_page,
                last_page: paginationData.last_page,
                from: paginationData.from,
                to: paginationData.to
              } : {
                total: companiesArray.length,
                per_page: params?.per_page || 10,
                current_page: params?.page || 1,
                last_page: Math.ceil((companiesArray.length || 0) / (params?.per_page || 10)),
                from: companiesArray.length > 0 ? 1 : 0,
                to: companiesArray.length
              },
              message: response.metadata?.message || 'Companies loaded successfully',
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  createCompany(companyData: {
    name: string;
    url?: string;
    phone_number?: string;
    linkedin?: string;
    company_identifier?: string;
    industry?: string;
    number_of_employees?: number;
    revenue?: number;
    about_company?: string;
    country?: string;
    region?: string;
    city?: string;
    address?: string;
    zip_code?: string;
  }): Observable<{ data: Company; message: string }> {
    const url = `${this.apiUrl}/crm-companies`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<any>(url, companyData, options).pipe(
          map((response) => {
            if (!response.data) {
              throw new Error('Failed to create company: ' + (response.metadata?.message || response.message));
            }
            return {
              data: response.data,
              message: response.metadata?.message || response.message,
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  updateCompany(id: number, companyData: {
    name?: string;
    url?: string;
    phone_number?: string;
    linkedin?: string;
    company_identifier?: string;
    industry?: string;
    number_of_employees?: number;
    revenue?: number;
    about_company?: string;
    country?: string;
    region?: string;
    city?: string;
    address?: string;
    zip_code?: string;
  }): Observable<{ data: Company; message: string }> {
    const url = `${this.apiUrl}/crm-companies/${id}`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.put<any>(url, companyData, options).pipe(
          map((response) => {
            if (!response.data) {
              throw new Error('Failed to update company: ' + (response.metadata?.message || response.message));
            }
            return {
              data: response.data,
              message: response.metadata?.message || response.message,
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  deleteCompany(id: number): Observable<{ message: string }> {
    const url = `${this.apiUrl}/crm-companies/${id}`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.delete<any>(url, options).pipe(
          map((response) => {
            return {
              message: response.metadata?.message || response.message,
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  getCompanyDetail(id: string): Observable<{ data: Company; message: string }> {
    const url = `${this.apiUrl}/crm-companies/${id}`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.get<any>(url, options).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Failed to load company details: Invalid response structure');
            }
            return {
              data: response.data,
              message: response.metadata?.message || 'Company details loaded successfully',
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  // Deal Methods
  getDeals(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    stage?: string;
    user_id?: number;
    filters?: {
      name?: string;
      deal_stage?: string;
    };
    sortBy?: 'created_at' | 'person_name' | 'company_name' | 'deal_name';
    sortDir?: 'asc' | 'desc';
    dateRange?: string;
  }): Observable<{
    data: any[];
    pagination: {
      total: number;
      per_page: number;
      current_page: number;
      last_page: number;
      from: number;
      to: number;
    };
    message: string;
  }> {
    const url = `${this.apiUrl}/crm-deals`;

    let httpParams = new HttpParams();
    // Sort — Deals' "Name" option maps to the `name` column.
    httpParams = httpParams.set('sort', this.toSpatieSort(params?.sortBy, params?.sortDir, 'name'));
    if (params?.page) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params?.per_page) {
      httpParams = httpParams.set('per_page', params.per_page.toString());
    }

    // User ID filter for data isolation
    if (params?.user_id) {
      httpParams = httpParams.set('filter[user_id]', params.user_id.toString());
    }

    // Date-range — only attach when non-default.
    if (params?.dateRange && params.dateRange !== 'all') {
      httpParams = httpParams.set('filter[date_range]', params.dateRange);
    }

    // Support new filter parameters
    if (params?.filters) {
      // Specific field filters (AND logic)
      if (params.filters.name) {
        httpParams = httpParams.set('filter[name]', params.filters.name);
      }
      if (params.filters.deal_stage) {
        httpParams = httpParams.set('filter[deal_stage]', params.filters.deal_stage);
      }
    } else if (params?.search) {
      // Search by name field only
      httpParams = httpParams.set('filter[name]', params.search);
    }

    // Keep stage parameter for backward compatibility (if not using filters)
    if (params?.stage && !params?.filters?.deal_stage) {
      httpParams = httpParams.set('filter[deal_stage]', params.stage);
    }

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.get<any>(url, {
          ...options,
          params: httpParams
        }).pipe(
          map((response) => {
            // console.log('Deals API Response:', response);
            if (!response || !response.data) {
              throw new Error('Failed to load deals: Invalid response structure');
            }

            // Check if the response has nested structure like contacts (response.data.data)
            // The user showed API response with data.data structure (Laravel pagination)
            let dealsArray: any[];
            let paginationData: any;

            if (response.data.data && Array.isArray(response.data.data)) {
              // Laravel pagination structure with nested data
              dealsArray = response.data.data;
              paginationData = response.data;
            } else if (Array.isArray(response.data)) {
              // Deals are directly in response.data
              dealsArray = response.data;
              paginationData = null;
            } else {
              throw new Error('Failed to load deals: Unexpected response structure');
            }

            return {
              data: dealsArray,
              pagination: paginationData ? {
                total: paginationData.total,
                per_page: paginationData.per_page,
                current_page: paginationData.current_page,
                last_page: paginationData.last_page,
                from: paginationData.from,
                to: paginationData.to
              } : {
                total: dealsArray.length,
                per_page: params?.per_page || 10,
                current_page: params?.page || 1,
                last_page: Math.ceil((dealsArray.length || 0) / (params?.per_page || 10)),
                from: dealsArray.length > 0 ? 1 : 0,
                to: dealsArray.length
              },
              message: response.metadata?.message || 'Deals loaded successfully',
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  createDeal(dealData: {
    name: string;
    deal_value: number;
    company_id: number;
    contact_id: number;
    icp_score?: number;
    lead_source?: string;
    user_id?: number;
    deal_stage?: string;
    workspace_id?: number;
    close_date?: string;
    type?: string;
  }): Observable<{ data: Deal; message: string }> {
    const url = `${this.apiUrl}/crm-deals`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<any>(url, dealData, options).pipe(
          map((response) => {
            if (!response.data) {
              throw new Error('Failed to create deal: ' + (response.metadata?.message || response.message));
            }
            return {
              data: response.data,
              message: response.metadata?.message || response.message,
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  updateDeal(id: number, dealData: {
    name?: string;
    deal_value?: number;
    company_id?: number;
    contact_id?: number;
    icp_score?: number;
    lead_source?: string;
    user_id?: number;
    deal_stage?: string;
    workspace_id?: number;
    close_date?: string;
    type?: string;
  }): Observable<{ data: Deal; message: string }> {
    const url = `${this.apiUrl}/crm-deals/${id}`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.put<any>(url, dealData, options).pipe(
          map((response) => {
            if (!response.data) {
              throw new Error('Failed to update deal: ' + (response.metadata?.message || response.message));
            }
            return {
              data: response.data,
              message: response.metadata?.message || response.message,
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  deleteDeal(id: number): Observable<{ message: string }> {
    const url = `${this.apiUrl}/crm-deals/${id}`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.delete<any>(url, options).pipe(
          map((response) => {
            return {
              message: response.metadata?.message || response.message,
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  getDealDetail(id: string): Observable<{ data: Deal; message: string }> {
    const url = `${this.apiUrl}/crm-deals/${id}`;

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.get<any>(url, options).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Failed to load deal details: Invalid response structure');
            }
            return {
              data: response.data,
              message: response.metadata?.message || 'Deal details loaded successfully',
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }

  // Save Report to CRM
  saveReportToCrm(
    searchId: number,
    config?: SaveToCrmConfig
  ): Observable<SaveToCrmResponse> {
    const url = `${this.apiUrl}/reports/${searchId}/save-to-crm`;

    const requestBody = config || {
      mapping_config: {
        create_companies: true,
        create_contacts: true,
        link_contacts_to_companies: true,
        default_lead_source: 'SalesOMMO Report'
      }
    };

    return this.getHttpOptions().pipe(
      switchMap(options =>
        this.http.post<any>(url, requestBody, options).pipe(
          map((response) => {
            if (!response || !response.data) {
              throw new Error('Failed to save report to CRM: Invalid response structure');
            }
            return {
              data: response.data,
              metadata: response.metadata
            };
          }),
          catchError((error) => throwError(() => error))
        )
      )
    );
  }
}
